import type { SxProps, Theme } from "@mui/material/styles";
import { matchPath } from "react-router-dom";

import { channelPath } from "@/hooks/dispatch/channelPath";
import { dispatchPagePath } from "@/hooks/dispatch/dispatchPanelRoute";

// Reserves space under the virtualized feed for the TTS composer at its
// resting (4-row) height. The composer auto-grows up to 8 rows and overlays
// the feed as it expands — feed items behind it stay in place — so this
// constant only needs to cover the resting chrome:
//   wrapper p:1 + 4-row textarea + pb:1 + divider + tips row + action row
//   (~185px) plus the parent's pb:2 (~16px) and a small buffer.
// Revisit if the composer's font size, padding, minRows, or action-row
// height changes.
export const feedFooterHeight = 210;

export const footerTransitionSx: SxProps<Theme> = { transition: "height 100ms ease-in" };

export const globalAudioPlayerHeight = 73;

export const topHeaderHeight = 80;
export const topHeaderHeightPx = `${topHeaderHeight}px`;
export const fullHeightMinusHeader = `calc(100vh - ${topHeaderHeight}px)`;

export const isDriverFeedPath = (pathname: string): boolean => /\/drivers\//.test(pathname);

export const isChannelFeedPath = (pathname: string): boolean => /\/channels\//.test(pathname);

/** The Dispatch page and its popover route; both own the whole main area rather than the split layout. */
export const isDispatchPath = (pathname: string): boolean => /\/dispatch(?:\/|$)/.test(pathname);

/**
 * Where a driver-view URL goes in a workspace with Dispatch on, which has no driver list; `null` for any other URL.
 *
 * A driver's channel opens that driver's History in Dispatch, through {@link channelPath}. Any other driver-view URL
 * opens the Dispatch page. `NewDesignLayout` renders the driver list from the pathname alone, with no route element
 * of its own, so the redirect is the layout's to make.
 */
export function dispatchPathForDriverView(pathname: string): string | null {
  if (!isDriverFeedPath(pathname)) return null;
  const channel = matchPath("/workspaces/:workspaceId/drivers/:workspaceMembershipId/channel/:feedId", pathname);
  const { workspaceId, workspaceMembershipId, feedId } = channel?.params ?? {};
  if (workspaceId && workspaceMembershipId && feedId) {
    return channelPath({ workspaceId, feedId, aliasMembershipId: workspaceMembershipId, dispatchEnabled: true });
  }
  const workspace = matchPath({ path: "/workspaces/:workspaceId", end: false }, pathname)?.params.workspaceId;
  return workspace ? dispatchPagePath(workspace, null) : null;
}
